import React, { useState } from 'react';
import { FaStar, FaChartLine, FaExclamationTriangle, FaCheckCircle, FaThumbsUp, FaThumbsDown } from 'react-icons/fa';

const SellerQuality = () => {
    const [timeRange, setTimeRange] = useState('30days');

    const qualityMetrics = {
        overallScore: 4.5,
        totalReviews: 156,
        orderFulfillment: 98.5,
        onTimeDelivery: 95.2,
        returnRate: 2.3,
        responseTime: '2.5 hrs',
    };

    const recentReviews = [
        { id: 1, product: 'Handwoven Silk Saree', rating: 5, comment: 'Beautiful craftsmanship, exceeded expectations!', customer: 'Priya M.', date: '2024-12-10' },
        { id: 2, product: 'Brass Lamp', rating: 4, comment: 'Good quality, packaging could be better.', customer: 'Rahul K.', date: '2024-12-09' },
        { id: 3, product: 'Pattachitra Painting', rating: 5, comment: 'Authentic Odisha art, love it!', customer: 'Meera S.', date: '2024-12-08' },
        { id: 4, product: 'Silver Anklet', rating: 3, comment: 'Size was smaller than expected.', customer: 'Lakshmi N.', date: '2024-12-07' },
    ];

    const renderStars = (rating) => {
        return [...Array(5)].map((_, i) => (
            <FaStar key={i} className={i < rating ? 'text-yellow-400' : 'text-gray-300'} />
        ));
    };

    return (
        <div className="p-6">
            <div className="mb-6">
                <h1 className="text-2xl font-bold text-gray-800">Quality Dashboard</h1>
                <p className="text-gray-600">Monitor your seller performance and customer feedback</p>
            </div>

            {/* Overall Score Card */}
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg p-6 text-white mb-6">
                <div className="flex items-center justify-between">
                    <div>
                        <p className="text-blue-100 text-sm">Overall Seller Rating</p>
                        <div className="flex items-center gap-3 mt-2">
                            <span className="text-5xl font-bold">{qualityMetrics.overallScore}</span>
                            <div className="flex">{renderStars(Math.round(qualityMetrics.overallScore))}</div>
                        </div>
                        <p className="text-blue-100 text-sm mt-2">Based on {qualityMetrics.totalReviews} reviews</p>
                    </div>
                    <div className="text-right">
                        <span className="bg-green-500 px-3 py-1 rounded-full text-sm font-medium">
                            Top Rated Seller
                        </span>
                    </div>
                </div>
            </div>

            {/* Metrics Grid */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-white p-4 rounded-lg shadow">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-gray-500 text-sm">Order Fulfillment</p>
                            <p className="text-2xl font-bold text-green-600">{qualityMetrics.orderFulfillment}%</p>
                        </div>
                        <FaCheckCircle className="text-3xl text-green-500" />
                    </div>
                    <div className="mt-2 h-2 bg-gray-200 rounded-full">
                        <div className="h-2 bg-green-500 rounded-full" style={{ width: `${qualityMetrics.orderFulfillment}%` }} />
                    </div>
                </div>

                <div className="bg-white p-4 rounded-lg shadow">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-gray-500 text-sm">On-Time Delivery</p>
                            <p className="text-2xl font-bold text-blue-600">{qualityMetrics.onTimeDelivery}%</p>
                        </div>
                        <FaChartLine className="text-3xl text-blue-500" />
                    </div>
                    <div className="mt-2 h-2 bg-gray-200 rounded-full">
                        <div className="h-2 bg-blue-500 rounded-full" style={{ width: `${qualityMetrics.onTimeDelivery}%` }} />
                    </div>
                </div>

                <div className="bg-white p-4 rounded-lg shadow">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-gray-500 text-sm">Return Rate</p>
                            <p className="text-2xl font-bold text-yellow-600">{qualityMetrics.returnRate}%</p>
                        </div>
                        <FaExclamationTriangle className="text-3xl text-yellow-500" />
                    </div>
                    <p className="text-xs text-green-600 mt-2">Below average (Good!)</p>
                </div>

                <div className="bg-white p-4 rounded-lg shadow">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-gray-500 text-sm">Avg Response Time</p>
                            <p className="text-2xl font-bold text-purple-600">{qualityMetrics.responseTime}</p>
                        </div>
                        <FaStar className="text-3xl text-purple-500" />
                    </div>
                    <p className="text-xs text-green-600 mt-2">Faster than 90% sellers</p>
                </div>
            </div>

            {/* Recent Reviews */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="px-6 py-4 border-b flex justify-between items-center">
                    <h2 className="text-lg font-semibold text-gray-800">Recent Customer Reviews</h2>
                    <select
                        value={timeRange}
                        onChange={(e) => setTimeRange(e.target.value)}
                        className="px-3 py-1 border border-gray-300 rounded-lg text-sm"
                    >
                        <option value="7days">Last 7 days</option>
                        <option value="30days">Last 30 days</option>
                        <option value="90days">Last 90 days</option>
                    </select>
                </div>
                <div className="divide-y">
                    {recentReviews.map((review) => (
                        <div key={review.id} className="p-6 hover:bg-gray-50">
                            <div className="flex justify-between items-start">
                                <div>
                                    <p className="font-semibold text-gray-800">{review.product}</p>
                                    <div className="flex items-center gap-2 mt-1">
                                        <div className="flex">{renderStars(review.rating)}</div>
                                        <span className="text-sm text-gray-500">by {review.customer}</span>
                                    </div>
                                    <p className="text-gray-600 mt-2">"{review.comment}"</p>
                                </div>
                                <div className="text-right">
                                    <span className="text-sm text-gray-500">{review.date}</span>
                                    <div className="flex gap-2 mt-2">
                                        <button className="p-2 text-green-600 hover:bg-green-50 rounded">
                                            <FaThumbsUp />
                                        </button>
                                        <button className="p-2 text-gray-400 hover:bg-gray-100 rounded">
                                            Reply
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default SellerQuality;
